import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUserProfile } from '@/hooks/useUserProfile';

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, signOut } = useAuth();
  const { data: profile, isLoading: profileLoading, isError } = useUserProfile();
  const location = useLocation();

  if (isLoading || (isAuthenticated && profileLoading)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  // If profile failed to load or doesn't exist, sign the user out
  if (isError || (!profileLoading && !profile)) {
    signOut();
    return <Navigate to="/auth" replace />;
  }

  if (profile) {
    // Block inactive users
    if (!(profile as any).is_active) {
      signOut();
      return <Navigate to="/auth" replace />;
    }

    const roles: string[] = (profile as any)?.user_role
      ?.map((ur: any) => ur.role?.name)
      .filter(Boolean) ?? [];

    const hasStaffRole = roles.some(r =>
      ["admin", "cellar_manager", "cellar_staff"].includes(r)
    );

    const isClientViewer = roles.includes("client_viewer");

    // Redirect client_viewer users who have no staff roles to the portal
    if (isClientViewer && !hasStaffRole && !location.pathname.startsWith("/portal")) {
      return <Navigate to="/portal" replace />;
    }
  }

  return <>{children}</>;
}
